import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import bill components (lazy-loaded for performance)
const Bills = React.lazy(() => import("./Bills"));
const NewBill = React.lazy(() => import("./NewBill"));
const BillDetails = React.lazy(() => import("./BillDetails"));
const BillPDFPreview = React.lazy(() => import("./BillPDFPreview"));
const PaymentReceivingDetails = React.lazy(() => import("./PaymentReceivingDetails"));
const EditPaymentDetails = React.lazy(() => import("./EditPaymentDetails"));
const CompanyDetails = React.lazy(() => import("./CompanyDetails"));

// Subscription components (consolidated from subscriptions module)
// SubscriptionsDashboard removed — thin stats page that overlaps with SubscriptionBilling
const ManageSubscriptions = React.lazy(() => import("./ManageSubscriptions"));
const CreateSubscription = React.lazy(() => import("./CreateSubscription"));
const EditSubscription = React.lazy(() => import("./EditSubscription"));
const PaymentMethods = React.lazy(() => import("./PaymentMethods"));
const ManualCharging = React.lazy(() => import("./ManualCharging"));
// BillingSettings removed — non-functional mockup, all settings handled in EditPaymentDetails

// SubscriptionBilling removed — consolidated into ManageSubscriptions page

// Subscription product definitions (admin CRUD)
const SubscriptionProducts = React.lazy(() => import("./SubscriptionProducts"));

// Notification rules
const BillNotificationRules = React.lazy(() => import("./BillNotificationRules"));

const billsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "bills",
    name: "Billing & Subscriptions",
    description: "Invoicing, subscription management, and payment processing",
    version: "2.0.0",
    enabled: true,
    category: "billing",
    order: 6,
    icon: "💳",
    color: "#E53E3E",

    // ROUTES
    routes: [
        // --- Invoicing ---
        {
            path: "/bills",
            component: Bills,
            permissionLevel: "view"
        },
        {
            path: "/bills/new",
            component: NewBill,
            permissionLevel: "create"
        },
        {
            path: "/bills/payment-details",
            component: PaymentReceivingDetails,
            permissionLevel: "edit"
        },
        {
            path: "/bills/payment-details/edit",
            component: EditPaymentDetails,
            permissionLevel: "edit"
        },
        {
            path: "/bills/company-details",
            component: CompanyDetails,
            permissionLevel: "edit"
        },
        {
            path: "/bill/:id",
            component: BillDetails,
            public: true
        },
        {
            path: "/bill/:id/preview",
            component: BillPDFPreview,
            public: true
        },
        // --- Subscriptions ---
        {
            path: "/bills/subscriptions/manage",
            component: ManageSubscriptions,
            permissions: ["ADMIN", "MANAGER"]
        },
        {
            path: "/bills/subscriptions/create",
            component: CreateSubscription,
            permissions: ["ADMIN"]
        },
        {
            path: "/bills/subscriptions/:id/edit",
            component: EditSubscription,
            permissions: ["ADMIN"]
        },
        {
            path: "/bills/subscriptions/payment-methods",
            component: PaymentMethods,
            permissions: ["ADMIN"]
        },
        {
            path: "/bills/subscriptions/manual-charging",
            component: ManualCharging,
            permissions: ["ADMIN"]
        },
        // --- Subscription Products ---
        {
            path: "/bills/subscription-products",
            component: SubscriptionProducts,
            permissions: ["ADMIN"]
        },
        // --- Notification Rules ---
        {
            path: "/bills/notification-settings",
            component: BillNotificationRules,
            permissionLevel: "edit"
        }
    ],

    // NAVIGATION
    navigation: [
        // Invoicing section
        {
            label: "All Bills",
            path: "/bills",
            icon: "📄",
            permissionLevel: "view"
        },
        {
            label: "New Bill",
            path: "/bills/new",
            icon: "➕",
            permissionLevel: "create"
        },
        {
            label: "Payment Details",
            path: "/bills/payment-details",
            icon: "💳",
            permissionLevel: "edit"
        },
        {
            label: "Company Details",
            path: "/bills/company-details",
            icon: "🏢",
            permissionLevel: "edit"
        },
        // Subscriptions section
        {
            label: "Subscriptions",
            path: "/bills/subscriptions/manage",
            icon: "📋",
            permissions: ["ADMIN", "MANAGER"]
        },
        {
            label: "Payment Methods",
            path: "/bills/subscriptions/payment-methods",
            icon: "💳",
            permissions: ["ADMIN"]
        },
        {
            label: "Manual Charging",
            path: "/bills/subscriptions/manual-charging",
            icon: "⚡",
            permissions: ["ADMIN"]
        },
        {
            label: "Sub Products",
            path: "/bills/subscription-products",
            icon: "🎯",
            permissions: ["ADMIN"]
        },
        {
            label: "Notifications",
            path: "/bills/notification-settings",
            icon: "🔔",
            permissionLevel: "edit"
        }
    ],

    // PERMISSIONS (resolved from backend single-source-of-truth)
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: []
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Create Bill",
            path: "/bills/new",
            icon: "➕",
            description: "Create a new invoice or bill"
        },
        {
            label: "New Subscription",
            path: "/bills/subscriptions/create",
            icon: "📋",
            description: "Create a new subscription"
        },
        {
            label: "Charge Client",
            path: "/bills/subscriptions/manual-charging",
            icon: "⚡",
            description: "Charge a client's saved payment method"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "2.0.0",
            releaseDate: "2026-02-27",
            title: "Billing & Subscriptions Consolidation",
            features: [
                "✅ Consolidated subscriptions module into billing",
                "✅ Consolidated subscription-billing dashboard into billing",
                "✅ Subscription product definitions (database-driven)",
                "✅ Removed Stripe env var fallbacks (tenant keys only)",
                "✅ Unified module breadcrumb navigation"
            ],
            notes: "Major consolidation of 3 separate billing/subscription modules into one unified Billing & Subscriptions module."
        },
        {
            version: "1.2.0",
            releaseDate: "2025-11-22",
            title: "Production Release",
            features: [
                "✅ Comprehensive changelog history documentation",
                "✨ Enhanced authentication module integration",
                "✨ Enhanced dark mode color handling across bill components"
            ],
            notes: "Production-ready release with complete feature history."
        }
    ],

    // ROADMAP
    roadmap: []

};

export default billsModuleConfig;
